import { useCallback, useEffect, useRef, useState } from "react";
import { Clock3, RefreshCw, UserRoundCheck, UsersRound } from "lucide-react";
import { api, useAuth } from "@/auth/AuthContext";
import { ProtectedBankPhoto } from "@/components/mantab/ProtectedBankPhoto";
import { formatCalendarDate } from "@/utils/dateDisplay";

const todayInJakarta = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Jakarta", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
};

const formatDuration = (seconds = 0) => {
  const safe = Math.max(0, Number(seconds) || 0);
  if (safe < 60) return `${safe} detik`;
  const minutes = Math.floor(safe / 60);
  const remainder = safe % 60;
  return remainder ? `${minutes} menit ${remainder} detik` : `${minutes} menit`;
};

export const MantabOperatorSummary = ({ schedules = [] }) => {
  const { user } = useAuth();
  const [date, setDate] = useState(todayInJakarta);
  const [scheduleId, setScheduleId] = useState("");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const requestSequence = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++requestSequence.current;
    setLoading(true); setError("");
    try {
      const response = await api.get("/mantab/bank/operator-summary", {
        params: { date, schedule_id: scheduleId || undefined },
      });
      if (requestId === requestSequence.current) setSummary(response.data);
    } catch (requestError) {
      if (requestId === requestSequence.current) {
        setError(requestError.response?.data?.detail || "Rekap operator tidak dapat dimuat.");
      }
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  }, [date, scheduleId]);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, 30000);
    return () => { window.clearInterval(timer); requestSequence.current += 1; };
  }, [load]);

  return <section className="mantab-operator-summary" data-testid="mantab-operator-summary">
    <div className="mantab-operator-summary-head">
      <div><UserRoundCheck size={18} /><span><strong>Rekap Waktu Layanan Operator</strong><small>{user?.role === "bank_admin" ? "Seluruh operator bank" : "Layanan Anda"}</small></span></div>
      <div className="mantab-operator-summary-filters">
        <label><span>Tanggal</span><input type="date" value={date} onChange={(event) => setDate(event.target.value)} data-testid="operator-summary-date" /></label>
        <label><span>Kegiatan</span><select value={scheduleId} onChange={(event) => setScheduleId(event.target.value)} data-testid="operator-summary-schedule"><option value="" label="Semua Kegiatan" />{schedules.map((item) => <option key={item.schedule_id} value={item.schedule_id} label={`${item.lokasi} · ${formatCalendarDate(item.tanggal, "short")}`} />)}</select></label>
        <button type="button" onClick={load} disabled={loading} aria-label="Perbarui rekap operator" title="Perbarui rekap" data-testid="operator-summary-refresh"><RefreshCw size={16} className={loading ? "is-spinning" : ""} /></button>
      </div>
    </div>
    {error && <div className="mantab-alert error" data-testid="operator-summary-error">{error}</div>}
    <div className="mantab-operator-summary-totals">
      <div><UsersRound size={18} /><span><small>TOTAL DILAYANI</small><strong data-testid="operator-summary-total">{summary?.total_served ?? 0}</strong></span></div>
      <div><Clock3 size={18} /><span><small>RATA-RATA WAKTU</small><strong data-testid="operator-summary-average">{formatDuration(summary?.average_service_seconds)}</strong></span></div>
    </div>
    {loading && !summary ? <div className="mantab-participant-loading" data-testid="operator-summary-loading">Memuat rekap operator…</div> : <div className="mantab-operator-summary-list" data-testid="operator-summary-list">
      {summary?.operators?.map((operator) => <article key={operator.operator_id} data-testid={`operator-summary-row-${operator.operator_id}`}>
        <ProtectedBankPhoto photoId={operator.operator_photo_id} alt={`Foto ${operator.operator_name}`} className="mantab-operator-summary-photo" dataTestId={`operator-summary-photo-${operator.operator_id}`} />
        <span><strong data-testid={`operator-summary-name-${operator.operator_id}`}>{operator.operator_name}</strong><small>{operator.operator_role === "bank_admin" ? "Admin Bank" : "Operator Bank"}</small></span>
        <div><small>TOTAL DILAYANI</small><strong data-testid={`operator-summary-total-${operator.operator_id}`}>{operator.total_served}</strong></div>
        <div><small>RATA-RATA WAKTU</small><strong data-testid={`operator-summary-average-${operator.operator_id}`}>{formatDuration(operator.average_service_seconds)}</strong></div>
      </article>)}
      {!summary?.operators?.length && <div className="mantab-empty" data-testid="operator-summary-empty">Belum ada operator yang dapat ditampilkan.</div>}
    </div>}
  </section>;
};